import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Save, Upload, Trash2, FileDown, Users, Download, TrendingUp,
  Monitor, Tablet, Smartphone, Eye, EyeOff,
} from "lucide-react";
import api, { BACKEND } from "@/lib/api";
import { Header } from "./Schools";

const SI = "px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm w-full focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 bg-white";
const PAGES = [
  { key: "home", label: "Home" },
  { key: "about", label: "About" },
  { key: "programs", label: "Programs" },
  { key: "achievements", label: "Achievements" },
  { key: "gallery", label: "Gallery" },
  { key: "podcast", label: "Podcast" },
];
const POSITIONS = [
  { value: "left-bottom", label: "Left Bottom" },
  { value: "left-center", label: "Left Center" },
  { value: "left-top", label: "Left Top" },
  { value: "right-bottom", label: "Right Bottom" },
  { value: "right-center", label: "Right Center" },
  { value: "right-top", label: "Right Top" },
];
const ANIMATIONS = [
  { value: "none", label: "None" },
  { value: "pulse", label: "Pulse" },
  { value: "bounce", label: "Bounce" },
  { value: "float", label: "Float" },
  { value: "glow", label: "Glow" },
];
const ICONS = [
  { value: "file-down", label: "File Down" },
  { value: "download", label: "Download" },
  { value: "file-text", label: "File Text" },
  { value: "book-open", label: "Book Open" },
  { value: "graduation-cap", label: "Graduation Cap" },
];

function Lbl({ children }) { return <label className="block text-xs font-semibold text-gray-700 mb-1">{children}</label>; }
function Card({ title, children }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
      <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50/60"><span className="text-sm font-semibold text-gray-700">{title}</span></div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}
function Toggle({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer py-1 select-none">
      <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)}
        className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${checked ? "bg-indigo-600" : "bg-gray-300"}`}>
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? "translate-x-4" : ""}`} />
      </button>
      <span className="text-sm font-medium text-gray-800">{label}</span>
    </label>
  );
}
function Row2({ children }) { return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>; }
function ColorRow({ label, value, onChange }) {
  return (
    <div>
      <Lbl>{label}</Lbl>
      <div className="flex gap-2">
        <input type="color" value={value || "#000000"} onChange={e => onChange(e.target.value)} className="w-10 h-10 rounded border border-gray-200 cursor-pointer shrink-0" />
        <input className={SI} value={value} onChange={e => onChange(e.target.value)} />
      </div>
    </div>
  );
}

export default function ProspectusWidget() {
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [tab, setTab] = useState("general");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  async function load() {
    setLoading(true);
    try {
      const [s, st, l] = await Promise.all([
        api.get("/admin/prospectus/settings"),
        api.get("/admin/prospectus/stats"),
        api.get("/admin/prospectus/leads"),
      ]);
      setSettings(s.data);
      setStats(st.data);
      setLeads(l.data || []);
    } catch { toast.error("Failed to load prospectus settings"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const set = (k, v) => setSettings(s => ({ ...s, [k]: v }));

  async function save() {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/prospectus/settings", settings);
      setSettings(data);
      toast.success("Settings saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  }

  async function handleUpload(file) {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Only PDF files are accepted"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/prospectus/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSettings(s => ({ ...s, pdf_url: data.url, pdf_filename: data.filename }));
      toast.success("Prospectus uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  }

  async function deletePdf() {
    if (!window.confirm("Remove the current prospectus PDF? The floating button will hide until a new one is uploaded.")) return;
    try {
      await api.delete("/admin/prospectus/pdf");
      setSettings(s => ({ ...s, pdf_url: "", pdf_filename: "" }));
      toast.success("PDF removed");
    } catch { toast.error("Delete failed"); }
  }

  function togglePage(key) {
    setSettings(s => {
      let pages = s.show_pages || ["all"];
      if (key === "all") return { ...s, show_pages: ["all"] };
      pages = pages.filter(p => p !== "all");
      pages = pages.includes(key) ? pages.filter(p => p !== key) : [...pages, key];
      return { ...s, show_pages: pages.length ? pages : ["all"] };
    });
  }

  const TABS = [
    { id: "general", label: "General" },
    { id: "style", label: "Style & Position" },
    { id: "visibility", label: "Visibility" },
    { id: "leads", label: "Leads" },
    { id: "analytics", label: "Analytics" },
  ];

  if (loading || !settings) {
    return (
      <div className="fade-up">
        <Header title="Floating Prospectus" subtitle="Configure the floating download widget." />
        <div className="mt-6 space-y-4 max-w-3xl">{[1, 2, 3].map(n => <div key={n} className="h-24 rounded-2xl bg-gray-100 animate-pulse" />)}</div>
      </div>
    );
  }

  return (
    <div className="fade-up pb-12">
      <Header
        title="Floating Prospectus"
        subtitle="A floating download button, similar to WhatsApp, for your website prospectus."
        actions={
          <button onClick={save} disabled={saving}
            className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-[#1e293b] transition disabled:opacity-60">
            <Save className="w-4 h-4" />{saving ? "Saving…" : "Save Settings"}
          </button>
        }
      />

      <div className="flex gap-0 mt-6 border-b border-gray-200 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition ${tab === t.id ? "border-indigo-600 text-indigo-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-5 max-w-3xl space-y-5">

        {tab === "general" && (<>
          <Card title="Enable / Disable">
            <Toggle label="Show Floating Prospectus Widget" checked={!!settings.enabled} onChange={v => set("enabled", v)} />
            {!settings.pdf_url && settings.enabled && (
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                Widget is enabled but no PDF is uploaded — it won't appear on the site until you upload one below.
              </p>
            )}
          </Card>

          <Card title="Prospectus PDF">
            {settings.pdf_url ? (
              <div className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3">
                <div className="flex items-center gap-2 min-w-0">
                  <FileDown className="w-4 h-4 text-indigo-600 shrink-0" />
                  <span className="text-sm text-gray-700 truncate">{settings.pdf_filename || "prospectus.pdf"}</span>
                </div>
                <div className="flex gap-2 shrink-0">
                  <a href={`${BACKEND}${settings.pdf_url}`} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:underline px-2 py-1">View</a>
                  <button onClick={deletePdf} className="text-xs text-rose-500 hover:underline px-2 py-1">Remove</button>
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-400">No PDF uploaded yet.</p>
            )}
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="inline-flex items-center gap-1.5 text-sm border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 px-4 py-2.5 rounded-lg transition disabled:opacity-50">
              <Upload className="w-4 h-4" />{uploading ? "Uploading…" : settings.pdf_url ? "Replace PDF" : "Upload PDF"}
            </button>
            <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={e => handleUpload(e.target.files?.[0])} />
          </Card>

          <Card title="Button Text">
            <div>
              <Lbl>Button Label</Lbl>
              <input className={SI} value={settings.button_label} onChange={e => set("button_label", e.target.value)} placeholder="Download Prospectus" />
            </div>
            <div>
              <Lbl>Tooltip</Lbl>
              <input className={SI} value={settings.tooltip} onChange={e => set("tooltip", e.target.value)} placeholder="Get our latest prospectus" />
            </div>
          </Card>

          <Card title="Icon">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {ICONS.map(i => (
                <button key={i.value} type="button" onClick={() => set("icon", i.value)}
                  className={`text-xs px-3 py-2.5 rounded-lg border transition ${settings.icon === i.value ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}>
                  {i.label}
                </button>
              ))}
            </div>
          </Card>

          <Card title="Lead Capture">
            <Toggle label="Require Visitor Details Before Download" checked={!!settings.require_lead_capture} onChange={v => set("require_lead_capture", v)} />
            <p className="text-xs text-gray-400">When enabled, visitors must submit Name, Email, Mobile, School, and City before the download starts.</p>
          </Card>
        </>)}

        {tab === "style" && (<>
          <Card title="Colors">
            <Row2>
              <ColorRow label="Background" value={settings.background} onChange={v => set("background", v)} />
              <ColorRow label="Icon / Text Color" value={settings.icon_color} onChange={v => set("icon_color", v)} />
            </Row2>
          </Card>

          <Card title="Shape & Shadow">
            <Row2>
              <div><Lbl>Border Radius (px)</Lbl><input type="number" className={SI} value={settings.border_radius} onChange={e => set("border_radius", parseInt(e.target.value) || 0)} /></div>
              <div><Lbl>Button Size (px)</Lbl><input type="number" className={SI} value={settings.button_size} onChange={e => set("button_size", parseInt(e.target.value) || 56)} /></div>
              <div><Lbl>Icon Size (px)</Lbl><input type="number" className={SI} value={settings.icon_size} onChange={e => set("icon_size", parseInt(e.target.value) || 22)} /></div>
              <div>
                <Lbl>Shadow</Lbl>
                <select className={SI} value={settings.shadow ? "on" : "off"} onChange={e => set("shadow", e.target.value === "on")}>
                  <option value="on">On</option><option value="off">Off</option>
                </select>
              </div>
            </Row2>
          </Card>

          <Card title="Animation">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {ANIMATIONS.map(a => (
                <button key={a.value} type="button" onClick={() => set("animation", a.value)}
                  className={`text-xs px-3 py-2 rounded-lg border transition ${settings.animation === a.value ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}>
                  {a.label}
                </button>
              ))}
            </div>
          </Card>

          <Card title="Position">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {POSITIONS.map(p => (
                <button key={p.value} type="button" onClick={() => set("position", p.value)}
                  className={`text-xs px-3 py-2.5 rounded-lg border transition ${settings.position === p.value ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}>
                  {p.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400">Only the offsets relevant to the chosen position apply, but all four are saved so switching position later won't lose your values.</p>
            <Row2>
              <div><Lbl>Top Offset (px)</Lbl><input type="number" className={SI} value={settings.distance_top} onChange={e => set("distance_top", parseInt(e.target.value) || 0)} /></div>
              <div><Lbl>Bottom Offset (px)</Lbl><input type="number" className={SI} value={settings.distance_bottom} onChange={e => set("distance_bottom", parseInt(e.target.value) || 0)} /></div>
              <div><Lbl>Left Offset (px)</Lbl><input type="number" className={SI} value={settings.distance_left} onChange={e => set("distance_left", parseInt(e.target.value) || 0)} /></div>
              <div><Lbl>Right Offset (px)</Lbl><input type="number" className={SI} value={settings.distance_right} onChange={e => set("distance_right", parseInt(e.target.value) || 0)} /></div>
            </Row2>
          </Card>

          <Card title="Stacking">
            <div><Lbl>Z-index</Lbl><input type="number" className={SI} value={settings.z_index} onChange={e => set("z_index", parseInt(e.target.value) || 55)} /></div>
            <p className="text-xs text-gray-400">Controls stacking order against other page elements. Higher values sit on top.</p>
          </Card>
        </>)}

        {tab === "visibility" && (<>
          <Card title="Device Visibility">
            <div className="grid grid-cols-3 gap-3">
              {[
                { key: "desktop_visible", label: "Desktop", Icon: Monitor },
                { key: "tablet_visible", label: "Tablet", Icon: Tablet },
                { key: "mobile_visible", label: "Mobile", Icon: Smartphone },
              ].map(({ key, label, Icon }) => (
                <button key={key} type="button" onClick={() => set(key, !settings[key])}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition ${settings[key] ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-gray-200 bg-white text-gray-400"}`}>
                  <Icon className="w-5 h-5" />
                  <span className="text-xs font-semibold">{label}</span>
                  <span className="text-[10px]">{settings[key] ? "Visible" : "Hidden"}</span>
                </button>
              ))}
            </div>
          </Card>

          <Card title="Page Visibility">
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => togglePage("all")}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${(settings.show_pages || []).includes("all") ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}>
                All Pages
              </button>
              {PAGES.map(p => (
                <button key={p.key} type="button" onClick={() => togglePage(p.key)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition ${(settings.show_pages || []).includes(p.key) ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}>
                  {p.label}
                </button>
              ))}
            </div>
          </Card>
        </>)}

        {tab === "leads" && (
          <Card title={`Captured Leads (${leads.length})`}>
            {leads.length === 0 ? (
              <p className="text-xs text-gray-400 py-6 text-center">No leads captured yet. Enable "Require Visitor Details" in General to start collecting them.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                      <th className="py-2 pr-4 font-medium">Name</th>
                      <th className="py-2 pr-4 font-medium">Email</th>
                      <th className="py-2 pr-4 font-medium">Mobile</th>
                      <th className="py-2 pr-4 font-medium">School</th>
                      <th className="py-2 pr-4 font-medium">City</th>
                      <th className="py-2 pr-4 font-medium">Page</th>
                      <th className="py-2 font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {leads.map(l => (
                      <tr key={l.id}>
                        <td className="py-2 pr-4 text-gray-700">{l.name}</td>
                        <td className="py-2 pr-4 text-gray-500">{l.email}</td>
                        <td className="py-2 pr-4 text-gray-500">{l.mobile || "—"}</td>
                        <td className="py-2 pr-4 text-gray-500">{l.school || "—"}</td>
                        <td className="py-2 pr-4 text-gray-500">{l.city || "—"}</td>
                        <td className="py-2 pr-4 text-gray-400 text-xs">{l.page || "—"}</td>
                        <td className="py-2 text-gray-400 text-xs whitespace-nowrap">{new Date(l.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}

        {tab === "analytics" && stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-gray-200 bg-white p-5">
              <div className="flex items-center justify-between mb-2">
                <Download className="w-4 h-4 text-indigo-600" />
                <span className="text-2xl font-bold text-gray-900">{stats.today}</span>
              </div>
              <div className="text-xs text-gray-500">Downloads Today</div>
            </div>
            <div className="rounded-2xl border border-gray-200 bg-white p-5">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
                <span className="text-2xl font-bold text-gray-900">{stats.this_month}</span>
              </div>
              <div className="text-xs text-gray-500">This Month</div>
            </div>
            <div className="rounded-2xl border border-gray-200 bg-white p-5">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-4 h-4 text-purple-600" />
                <span className="text-2xl font-bold text-gray-900">{stats.total}</span>
              </div>
              <div className="text-xs text-gray-500">Total Downloads</div>
            </div>
            <div className="rounded-2xl border border-gray-200 bg-white p-5 col-span-2 sm:col-span-3">
              <div className="text-sm font-semibold text-gray-700 mb-3">Top Downloaded Pages</div>
              {stats.top_pages?.length ? (
                <div className="space-y-2">
                  {stats.top_pages.map((p, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 capitalize">{p.page}</span>
                      <span className="text-gray-900 font-medium">{p.downloads}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-xs text-gray-400 py-4 text-center">No downloads yet</p>}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
